import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Branch } from "../../master-data/master-data.model";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  Validators,
} from "@angular/forms";
import { Subject } from "rxjs";
import { UsersService } from "../users.service";
import { MasterDataService } from "../../master-data/master-data-service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  AdminType,
  BR_ADMIN_UUID,
  CHAIN_ADMIN_UUID,
  CreateUserCommand,
  User,
} from "../users.model";
import { takeUntil } from "rxjs/operators";
import { ErrorStateMatcher } from "@angular/material/core";
import { Chain } from "../../chains/chains.model";
import { ChainsService } from "../../chains/chains.service";
import { NEW_UI_FORM_FIELD_DEFAULTS } from "../../shared/styles/new-ui-form-field";

@Component({
  selector: "app-admin-edit",
  templateUrl: "./admin-edit.component.html",
  styleUrls: ["./admin-edit.component.scss"],
  providers: [NEW_UI_FORM_FIELD_DEFAULTS],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdminEditComponent implements OnInit, OnDestroy {
  private usersService = inject(UsersService);
  private masterDataService = inject(MasterDataService);
  private chainsService = inject(ChainsService);
  private formBuilder = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<AdminEditComponent>>(MatDialogRef);
  data = inject<{
    type: AdminType;
    user: User;
  }>(MAT_DIALOG_DATA, { optional: true });

  AdminT = AdminType;
  branches: Branch[] = [];
  chains: Chain[] = [];
  adminForm = this.formBuilder.group({
    uuid: [null],
    name: ["", Validators.required],
    phone: [
      "",
      [Validators.required, Validators.pattern("^\\+[1-9]\\d{1,14}$")],
    ],
    email: ["", [Validators.required, Validators.email]],
    branch: [""],
    chains: [],
    externalId: [""],
    passGroup: this.formBuilder.group(
      {
        password: [
          "",
          [
            Validators.required,
            Validators.minLength(6),
            Validators.pattern(
              "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])[a-zA-Z0-9]+$",
            ),
          ],
        ],
        confirmPass: [""],
      },
      { validator: this.checkPasswords },
    ),
  });

  destroyed$ = new Subject();
  editMode = false;
  changePassword = true;

  matcher = new MyErrorStateMatcher();

  ngOnInit() {
    this.buildForm();
    this.patchForm();

    if (this.data.type === AdminType.BranchAdmin) {
      this.loadBranches();
    } else if (this.data.type === AdminType.ChainAdmin) {
      this.loadChains();
    }
  }

  private loadBranches() {
    this.masterDataService
      .getBranches()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((brs) => {
        this.branches = brs;
      });
  }

  private loadChains() {
    this.chainsService
      .getAllChains()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((chs) => {
        this.chains = chs;
      });
  }

  private buildForm() {
    if (this.data.type === AdminType.BranchAdmin) {
      this.adminForm.get("branch").setValidators([Validators.required]);
    } else if (this.data.type === AdminType.ChainAdmin) {
      this.adminForm.get("chains").setValidators([Validators.required]);
    }
    this.adminForm.updateValueAndValidity();
  }

  private patchForm() {
    if (this.data.user.email !== undefined && this.data.user.email !== "") {
      this.editMode = true;
    }

    if (this.editMode) {
      this.adminForm.get("uuid").patchValue(this.data.user.uuid);
      this.adminForm.get("email").patchValue(this.data.user.email);
      this.adminForm.get("email").disable();
      this.adminForm.get("name").patchValue(this.data.user.name);
      this.adminForm.get("phone").patchValue(this.data.user.phone);
      this.adminForm.get("externalId").patchValue(this.data.user.externalId);
      if (
        this.data.type === AdminType.BranchAdmin &&
        this.data.user.branch != null
      ) {
        this.adminForm.get("branch").patchValue(this.data.user.branch.uuid);
      }
      if (
        this.data.type === AdminType.ChainAdmin &&
        this.data.user.chains != null
      ) {
        this.adminForm.get("chains").patchValue(this.data.user.chains);
      }
      this.changePassword = false;
      this.removePassValidators();
    }
  }

  private removePassValidators() {
    const form = this.adminForm.get("passGroup");
    form.clearValidators();
    form.get("password").clearValidators();
    form.updateValueAndValidity();
  }

  private restorePassValidators() {
    const form = this.adminForm.get("passGroup");
    if (form) {
      form.setValidators(this.checkPasswords);

      form
        .get("password")
        ?.setValidators([
          Validators.required,
          Validators.pattern("^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{7,32}$"),
        ]);

      form.updateValueAndValidity();
    }
  }

  checkPasswords(group: AbstractControl) {
    const pass = group.get("password")?.value;
    const confirmPass = group.get("confirmPass")?.value;

    return pass === confirmPass ? null : { notSame: true };
  }

  onLockUser() {
    // TODO
  }

  onChangePassword() {
    this.changePassword = true;
    this.restorePassValidators();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSave() {
    const role =
      this.data.type === AdminType.ChainAdmin
        ? CHAIN_ADMIN_UUID
        : BR_ADMIN_UUID;
    const brUuid =
      this.data.type === AdminType.BranchAdmin
        ? this.adminForm.get("branch").value
        : null;
    const chnsUuids =
      this.data.type === AdminType.ChainAdmin
        ? this.adminForm.get("chains").value
        : [];
    const user: CreateUserCommand = {
      name: this.adminForm.get("name").value,
      email: this.adminForm.get("email").value,
      phone: this.adminForm.get("phone").value,
      areaUuid: null,
      branchUuid: brUuid,
      chainsUuids: chnsUuids,
      rolesUuid: [role],
      externalId: this.adminForm.get("externalId").value,
      password: this.adminForm.get("passGroup.password").value,
      warehouseUuid: null,
    };
    if (this.editMode) {
      user.uuid = this.adminForm.get("uuid").value;
    }
    this.dialogRef.close(user);
  }

  ngOnDestroy() {
    this.destroyed$.next(1);
    this.destroyed$.complete();
  }

  clearBranch() {
    this.adminForm.controls.branch.setValue(null);
  }

  clearChain() {
    this.adminForm.controls.chains.setValue(null);
  }
}

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const invalidCtrl = !!(control && control.invalid && control.parent.dirty);
    const invalidParent = !!(
      control &&
      control.parent &&
      control.parent.invalid &&
      control.parent.dirty
    );

    return invalidCtrl || invalidParent;
  }
}
